import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  Box,
  Button,
  IconButton,
  Portal,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { TooltipProps } from "@mui/material/Tooltip";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import { APP_FONT_FAMILY } from "../../constants/fonts";

type SetupCoachmarkProps = {
  open: boolean;
  title: string;
  subtitle: string;
  onDismiss: () => void;
  onNext?: () => void;
  nextLabel?: string;
  /** Stretch highlight to fill parent width (forms / sections). */
  fullWidth?: boolean;
  /**
   * When true (default), dims the full viewport with a hole over the target.
   * Set false inside Dialogs so the dim stays local to the dialog content.
   */
  pageDim?: boolean;
  /**
   * `menu` — safe inside MuiMenu (no giant spotlight shadow; that breaks Popover position).
   * `default` — page spotlight with dimmed surroundings.
   */
  variant?: "default" | "menu";
  /** Tighter spotlight — for tab labels and small controls. */
  compact?: boolean;
  /** Override tooltip placement (e.g. `right` for sidebar items). */
  placement?: TooltipProps["placement"];
  /** Highlighted target (page primary action or form section). */
  children: ReactElement;
};

type SpotlightRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type TipPlace = "top" | "bottom" | "left" | "right";

function asTipPlace(value: TooltipProps["placement"] | undefined): TipPlace {
  if (value === "bottom" || value === "left" || value === "right") return value;
  return "top";
}

function fallbackPlaces(preferred: TipPlace): TipPlace[] {
  if (preferred === "right") return ["right", "left", "top", "bottom"];
  if (preferred === "left") return ["left", "right", "top", "bottom"];
  if (preferred === "bottom") return ["bottom", "top", "left", "right"];
  // Prefer beside the target before dropping below (avoids tip jumping under).
  return ["top", "left", "right", "bottom"];
}

function pickTipPlace(
  preferred: TipPlace,
  anchor: SpotlightRect,
  tipW: number,
  tipH: number,
  gap: number,
  edge: number,
  vw: number,
  vh: number,
): TipPlace {
  const space: Record<TipPlace, number> = {
    top: anchor.top - edge,
    bottom: vh - (anchor.top + anchor.height) - edge,
    left: anchor.left - edge,
    right: vw - (anchor.left + anchor.width) - edge,
  };
  const need: Record<TipPlace, number> = {
    top: tipH + gap,
    bottom: tipH + gap,
    left: tipW + gap,
    right: tipW + gap,
  };
  for (const place of fallbackPlaces(preferred)) {
    if (space[place] >= need[place]) return place;
  }
  return (Object.entries(space) as [TipPlace, number][]).sort(
    (a, b) => b[1] - a[1],
  )[0][0];
}

function tipCoords(
  place: TipPlace,
  anchor: SpotlightRect,
  tipW: number,
  tipH: number,
  gap: number,
  edge: number,
  vw: number,
  vh: number,
): { top: number; left: number } {
  let top = 0;
  let left = 0;
  if (place === "right") {
    left = anchor.left + anchor.width + gap;
    top = anchor.top + anchor.height / 2 - tipH / 2;
  } else if (place === "left") {
    left = anchor.left - gap - tipW;
    top = anchor.top + anchor.height / 2 - tipH / 2;
  } else if (place === "bottom") {
    top = anchor.top + anchor.height + gap;
    left = anchor.left + anchor.width / 2 - tipW / 2;
  } else {
    top = anchor.top - gap - tipH;
    left = anchor.left + anchor.width / 2 - tipW / 2;
  }
  const maxLeft = Math.max(edge, vw - edge - tipW);
  const maxTop = Math.max(edge, vh - edge - tipH);
  return {
    left: Math.min(Math.max(edge, left), maxLeft),
    top: Math.min(Math.max(edge, top), maxTop),
  };
}

function CoachmarkPortalTip({
  anchor,
  preferredPlacement,
  zIndex,
  isMobile,
  isDark,
  children,
}: {
  anchor: SpotlightRect;
  preferredPlacement: TipPlace;
  zIndex: number;
  isMobile: boolean;
  isDark: boolean;
  children: ReactNode;
}) {
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el) return;

    const gap = 14;
    const edge = 12;
    const update = () => {
      const tipW = Math.max(el.offsetWidth, 200);
      const tipH = Math.max(el.offsetHeight, 72);
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const place = pickTipPlace(
        preferredPlacement,
        anchor,
        tipW,
        tipH,
        gap,
        edge,
        vw,
        vh,
      );
      setBox(tipCoords(place, anchor, tipW, tipH, gap, edge, vw, vh));
    };

    update();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [anchor.top, anchor.left, anchor.width, anchor.height, preferredPlacement]);

  return (
    <Box
      ref={tipRef}
      sx={{
        position: "fixed",
        top: box?.top ?? -9999,
        left: box?.left ?? -9999,
        visibility: box ? "visible" : "hidden",
        zIndex,
        maxWidth: isMobile ? "calc(100vw - 32px)" : 420,
        width: "max-content",
        px: 2.5,
        py: 1.75,
        bgcolor: "background.paper",
        color: "text.primary",
        borderRadius: "14px",
        border: 1,
        borderColor: "divider",
        boxShadow: isDark
          ? `0 12px 36px ${alpha("#000", 0.55)}`
          : "0 10px 32px rgba(15, 23, 42, 0.16)",
        pointerEvents: "auto",
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Spotlight onboarding hint: everything is dimmed except the target and tip.
 */
export function SetupCoachmark({
  open,
  title,
  subtitle,
  onDismiss,
  onNext,
  nextLabel,
  fullWidth = false,
  pageDim = true,
  variant = "default",
  compact = false,
  placement: placementProp,
  children,
}: SetupCoachmarkProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const dim = isDark
    ? alpha(theme.palette.common.black, 0.72)
    : alpha("#0f172a", 0.58);
  const highlightRing = isDark
    ? theme.palette.primary.main
    : theme.palette.common.white;
  const isMenu = variant === "menu";
  const usePageDim = pageDim && !isMenu;
  const pad = compact ? 4 : 8;
  const radius = isMenu ? 0 : compact ? 10 : 14;
  const targetRef = useRef<HTMLDivElement | null>(null);
  const [rects, setRects] = useState<SpotlightRect[]>([]);
  const maskIdRef = useRef(
    `setup-coachmark-mask-${Math.random().toString(36).slice(2, 9)}`,
  );
  const placement =
    placementProp ??
    (isMenu ? (isMobile ? "top" : "right") : "top");

  useLayoutEffect(() => {
    if (!open || !usePageDim) {
      setRects([]);
      return;
    }

    const update = () => {
      const el = targetRef.current;
      if (!el) return;

      const holes = Array.from(
        el.querySelectorAll<HTMLElement>("[data-setup-hole]"),
      );
      const sources = holes.length > 0 ? holes : [el];
      const next: SpotlightRect[] = [];
      for (const source of sources) {
        const r = source.getBoundingClientRect();
        // Skip off-screen / unpositioned targets (common while MuiMenu animates).
        if (r.width <= 0 || r.height <= 0) continue;
        if (r.bottom < 0 || r.top > window.innerHeight) continue;
        next.push({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
        });
      }
      setRects((prev) => {
        if (
          prev.length === next.length &&
          prev.every(
            (p, i) =>
              Math.abs(p.top - next[i].top) < 0.5 &&
              Math.abs(p.left - next[i].left) < 0.5 &&
              Math.abs(p.width - next[i].width) < 0.5 &&
              Math.abs(p.height - next[i].height) < 0.5,
          )
        ) {
          return prev;
        }
        return next;
      });
    };

    update();
    // Popover/Menu positions via transform — ResizeObserver won't see it, so
    // remeasure for a short window after open.
    let frames = 0;
    let raf = 0;
    const tick = () => {
      update();
      frames += 1;
      if (frames < 20) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    const interval = window.setInterval(update, 50);
    const stopInterval = window.setTimeout(() => window.clearInterval(interval), 500);

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (targetRef.current) ro?.observe(targetRef.current);
    targetRef.current
      ?.querySelectorAll("[data-setup-hole]")
      .forEach((node) => ro?.observe(node));
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearInterval(interval);
      window.clearTimeout(stopInterval);
      ro?.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, usePageDim, title, compact, fullWidth]);

  if (!open) {
    return children;
  }

  const singleRect = rects.length === 1 ? rects[0] : null;
  const multiRects = rects.length > 1 ? rects : null;
  const tipAnchorRect =
    rects.length > 0
      ? (() => {
          const top = Math.min(...rects.map((r) => r.top));
          const left = Math.min(...rects.map((r) => r.left));
          const right = Math.max(...rects.map((r) => r.left + r.width));
          const bottom = Math.max(...rects.map((r) => r.top + r.height));
          return {
            top,
            left,
            width: Math.max(0, right - left),
            height: Math.max(0, bottom - top),
          };
        })()
      : null;
  // Always portal the tip when page-dimming: keep ref on a plain Box (reliable
  // measure inside MuiMenu) and only show the tip once the hole rect exists.
  const usePortalTipAnchor = Boolean(usePageDim && tipAnchorRect);

  const tip = (
    <Box sx={{ position: "relative", textAlign: "center", pt: 0.5 }}>
      <IconButton
        aria-label={t("common.close")}
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        sx={{
          position: "absolute",
          top: -10,
          insetInlineEnd: -10,
          color: "text.disabled",
          p: 0.5,
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Typography
        sx={{
          fontFamily: APP_FONT_FAMILY,
          fontSize: "0.9375rem",
          fontWeight: 600,
          lineHeight: 1.35,
          color: "text.primary",
          mb: 0.5,
          px: 2.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: APP_FONT_FAMILY,
          fontSize: "0.8125rem",
          fontWeight: 400,
          lineHeight: 1.45,
          color: "text.secondary",
          mb: onNext ? 1.5 : 0,
          px: 1,
        }}
      >
        {subtitle}
      </Typography>
      {onNext ? (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          sx={{ textTransform: "none", borderRadius: "10px", px: 2 }}
        >
          {nextLabel ?? t("selfSetup.tourNext")}
        </Button>
      ) : null}
    </Box>
  );

  const tooltipSlotProps = {
    popper: {
      sx: { zIndex: (t: { zIndex: { modal: number } }) => t.zIndex.modal + 4 },
      modifiers: [
        {
          name: "flip",
          options: {
            // Keep beside left/right targets — flipping to top jumps menu tips
            // to the viewport edge.
            fallbackPlacements:
              placement === "right"
                ? ["left"]
                : placement === "left"
                  ? ["right"]
                  : placement === "top"
                    ? ["bottom", "right", "left"]
                    : placement === "bottom"
                      ? ["top", "right", "left"]
                      : ["right", "left", "bottom", "top"],
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 12,
            altAxis: true,
            boundary: "viewport",
            tether: false,
          },
        },
      ],
    },
    tooltip: {
      sx: {
        maxWidth: isMobile ? "calc(100vw - 32px)" : 420,
        width: "max-content",
        px: 2.5,
        py: 1.75,
        bgcolor: "background.paper",
        color: "text.primary",
        borderRadius: "14px",
        border: 1,
        borderColor: "divider",
        boxShadow: isDark
          ? `0 12px 36px ${alpha("#000", 0.55)}`
          : "0 10px 32px rgba(15, 23, 42, 0.16)",
      },
    },
    arrow: {
      sx: {
        color: "background.paper",
        "&::before": {
          border: `1px solid ${theme.palette.divider}`,
          boxSizing: "border-box",
        },
      },
    },
  };

  const targetBoxSx = {
    display: fullWidth || isMenu ? "block" : "inline-flex",
    width: fullWidth || isMenu ? "100%" : "auto",
    maxWidth: "100%",
    borderRadius: isMenu ? 0 : compact ? "10px" : "14px",
    position: "relative" as const,
    zIndex: isMenu ? 2 : 1,
    p: isMenu ? 0 : usePageDim ? 0 : compact ? 0.35 : 1.25,
    bgcolor: isMenu
      ? alpha(theme.palette.primary.main, 0.08)
      : "background.paper",
    outline: isMenu
      ? `2px solid ${theme.palette.primary.main}`
      : usePageDim
        ? "none"
        : `3px solid ${highlightRing}`,
    outlineOffset: isMenu ? "-2px" : compact ? "1px" : "2px",
    boxShadow: isMenu
      ? "none"
      : usePageDim
        ? "none"
        : `0 0 0 9999px ${dim}, 0 8px 28px ${alpha(
            theme.palette.primary.main,
            isDark ? 0.45 : 0.35,
          )}`,
  };

  const dimZ = theme.zIndex.tooltip + 2;
  const tipZ = theme.zIndex.tooltip + 4;

  const portalTip =
    usePortalTipAnchor && tipAnchorRect ? (
      <CoachmarkPortalTip
        anchor={tipAnchorRect}
        preferredPlacement={asTipPlace(placement)}
        zIndex={tipZ}
        isMobile={isMobile}
        isDark={isDark}
      >
        {tip}
      </CoachmarkPortalTip>
    ) : null;

  if (usePageDim) {
    const holeRects = multiRects ?? (singleRect ? [singleRect] : null);

    return (
      <>
        {holeRects ? (
          <Portal>
            <Box
              aria-hidden
              component="svg"
              width="100%"
              height="100%"
              sx={{
                position: "fixed",
                inset: 0,
                zIndex: dimZ,
                pointerEvents: "none",
              }}
            >
              <defs>
                <mask id={maskIdRef.current}>
                  <rect width="100%" height="100%" fill="#fff" />
                  {holeRects.map((r, i) => (
                    <rect
                      key={i}
                      x={Math.max(0, r.left - pad)}
                      y={Math.max(0, r.top - pad)}
                      width={r.width + pad * 2}
                      height={r.height + pad * 2}
                      rx={radius}
                      ry={radius}
                      fill="#000"
                    />
                  ))}
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill={dim}
                mask={`url(#${maskIdRef.current})`}
              />
              {holeRects.map((r, i) => (
                <rect
                  key={`ring-${i}`}
                  x={Math.max(0, r.left - pad)}
                  y={Math.max(0, r.top - pad)}
                  width={r.width + pad * 2}
                  height={r.height + pad * 2}
                  rx={radius}
                  ry={radius}
                  fill="none"
                  stroke={highlightRing}
                  strokeWidth={3}
                />
              ))}
            </Box>
          </Portal>
        ) : null}

        <Box ref={targetRef} sx={targetBoxSx}>
          {children}
        </Box>
        {portalTip ? <Portal>{portalTip}</Portal> : null}
      </>
    );
  }

  return (
    <Tooltip
      open
      arrow
      placement={placement}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={tip}
      TransitionProps={{ timeout: 0 }}
      slotProps={tooltipSlotProps}
    >
      <Box ref={targetRef} sx={targetBoxSx}>
        {children}
      </Box>
    </Tooltip>
  );
}